import styled, { CSSObject } from '@emotion/styled'
import { WebLink } from '@news-mono/web-common'
import { IconFacebook } from '../../icons/IconFacebook/IconFacebook'
import { IconInstagram } from '../../icons/IconInstagram/IconInstagram'
import { IconTwitter } from '../../icons/IconTwitter/IconTwitter'
import { colors } from '../../__styling/settings/colors'
import { fonts } from '../../__styling/settings/fonts'
import { metrics } from '../../__styling/settings/metrics'
import { breakpoint, breakpointMax } from '../../__styling/style-functions'
import { calcRem } from '../../__styling/style-functions/calc-rem'
import { textCrop } from '../../__styling/style-mixins'
import { themedValue } from '../../__styling/themed-value'

export const StyledBioBlock = styled('div')((props) => [
    {
        display: 'flex',
        flexWrap: 'wrap',
        background: colors.white,
    },
    ({ theme }) => ({
        marginBottom: themedValue(theme, {
            thewest: metrics.thewest.margins.sm,
            perthnow: metrics.perthnow.margins.sm,
            fallback: undefined,
        }),
    }),
    props.theme.kind === 'sevennews' && {
        flexDirection: 'column',
        flexWrap: 'nowrap',

        [breakpoint('md')]: {
            flexWrap: 'wrap',
            flexDirection: 'row',
            alignItems: 'center',
            paddingRight: calcRem(16),
        },
    },
])

export const StyledText = styled('div')(({ theme }) => [
    theme.kind === 'thewest' && {
        flex: '1 2 100%',
        padding: `${calcRem(40, 48)}`,
        alignSelf: 'center',
        order: 0,

        [breakpoint('sm')]: {
            order: 0,
            flex: '1 1 auto',
            width: '50%',
            padding: `${calcRem(32, 33)}`,
        },
        [breakpoint('md')]: {
            padding: `${calcRem(50, 58)}`,
        },

        [breakpoint('lg')]: {
            padding: `${calcRem(45, 65)}`,
        },
    },
    theme.kind === 'perthnow' && {
        padding: calcRem(
            metrics.perthnow.margins.md,
            metrics.perthnow.margins.md,
            0,
            metrics.perthnow.margins.md,
        ),
        width: '100%',
        borderBottom: `1px solid ${metrics.perthnow.margins.sm}`,
    },
    theme.kind === 'sevennews' && {
        padding: calcRem(16, 0, 22),
        marginTop: calcRem(20),
        borderTop: `1px solid ${colors.sevennews.alto}`,
        width: '100%',

        [breakpointMax('md')]: {
            marginTop: calcRem(16),
            padding: calcRem(6, 0, 0),
        },
    },
])

export const StyledAuthor = styled('h1')(
    {
        margin: 0,
        position: 'relative',
    },
    ({ theme }) => [
        theme.kind === 'thewest' && {
            textTransform: 'uppercase',
            lineHeight: 1,
            display: 'flex',
            flexDirection: 'column',
            fontSize: calcRem(28),
            fontFamily: fonts.thewest.sansSerifCond,
            color: colors.thewest.greyGorilla,

            [breakpoint('md')]: {
                fontSize: calcRem(36),
            },

            '&::after': {
                content: `''`,
                width: '4px',
                height: '100%',
                position: 'absolute',
                left: calcRem(-13),
                top: 0,
                background: theme.sectionValues.primaryColor,
            },
        },
        theme.kind === 'perthnow' && {
            borderBottom: `4px solid ${colors.perthnow.greyPyrite}`,
            paddingBottom: calcRem(metrics.perthnow.margins.sm),
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            lineHeight: 1,
            transform: `translateY(${calcRem(-metrics.perthnow.textOffset)})`,
            fontSize: calcRem(32),
            fontFamily: fonts.perthnow.heading,
            color: colors.perthnow.greyCoal,

            [breakpoint('md')]: {
                fontSize: calcRem(42),
            },
        },
        theme.kind === 'sevennews' && {
            textAlign: 'center',
            paddingTop: calcRem(18),
            [breakpoint('sm')]: {
                textAlign: 'left',
                paddingTop: calcRem(25),
            },
        },
    ],
)

export const StyledName = styled('span')(
    {
        display: 'block',
        flexBasis: '100%',
    },
    ({ theme }) => [
        theme.kind === 'thewest' && {
            marginBottom: calcRem(5),
            fontSize: calcRem(36),
            flex: '0 0 auto',

            ...textCrop({
                fontSize: 36,
                desiredLineHeight: 1.2,
                cropLineHeight: 1.2,
                topCrop: 10,
                bottomCrop: 8,
            }),

            [breakpoint('md')]: {
                marginBottom: calcRem(14),
            },
        },
        theme.kind === 'sevennews' && {
            marginBottom: calcRem(16),
            fontSize: calcRem(36),
            fontFamily: fonts.sevennews.sansSerifCond,
            fontWeight: 'bold',
            flex: '0 0 auto',

            ...textCrop({
                fontSize: 36,
                desiredLineHeight: 1.2,
                cropLineHeight: 1.2,
                topCrop: 10,
                bottomCrop: 8,
            }),
        },
        theme.kind === 'perthnow' && {
            lineHeight: 1.3,
        },
    ],
)

export const StyledPosition = styled('span')(
    {
        display: 'block',
        color: colors.thewest.greyKoala,
    },
    ({ theme }) => [
        theme.kind === 'thewest' && {
            textTransform: 'none',
            fontWeight: 700,
            color: colors.thewest.greyKoala,
            fontFamily: fonts.thewest.serif,
            fontSize: calcRem(18),
        },
        theme.kind === 'perthnow' && {
            lineHeight: 1,
            fontWeight: 400,
            fontSize: calcRem(14),
            color: colors.perthnow.greyMoonstone,
            fontFamily: fonts.perthnow.sansSerif,
        },
        theme.kind === 'sevennews' && {
            lineHeight: 1,
            fontWeight: 400,
            fontSize: calcRem(13),
            color: colors.sevennews.charade,
            fontFamily: fonts.sevennews.sansSerif,
        },
    ],
)

export const StyledFlexRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    [breakpointMax('xxs')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
})

export const StyledButtons = styled('span')({
    display: 'flex',
    [breakpointMax('xxs')]: {
        marginTop: 10,
    },
})

export const StyledBioText = styled('p')(
    {
        lineHeight: 1.375,
        margin: `${calcRem(16, 0, 21)}`,
    },
    ({ theme }) => [
        theme.kind === 'thewest' && {
            color: colors.thewest.greyElephant,
            lineHeight: '1.375',
        },
        theme.kind === 'sevennews' && {
            lineHeight: '1.875',
            margin: calcRem(16, 0, 10),
            [breakpointMax('md')]: {
                marginBottom: calcRem(24),
            },
        },
        theme.kind === 'perthnow' && {
            color: colors.perthnow.greyCoal,
            lineHeight: '1.625',
        },
    ],
)

const desktopImageSize = 292
export const StyledImageContainer = styled('div')(
    {
        flex: '1 0 auto',
        width: '100%',
    },
    ({ theme }) => [
        theme.kind === 'thewest' && {
            flex: '0 0 auto',
            position: 'relative',
            width: '100%',
            height: 'auto',

            [breakpoint('sm')]: {
                order: 1,
                margin: '38px 60px 38px 0',
                width: calcRem(desktopImageSize),
                height: calcRem(desktopImageSize),
                overflow: 'hidden',
                borderRadius: '50%',
            },
        },
        theme.kind === 'sevennews' && {
            flex: '0 0 auto',
            position: 'relative',
            alignSelf: 'center',
            order: 0,
            margin: calcRem(0, 24, 0, 10),
            width: calcRem(153),
            height: calcRem(153),
            overflow: 'hidden',
            borderRadius: '50%',

            [breakpoint('md')]: {
                marginBottom: 0,
                width: calcRem(111),
                height: calcRem(111),
            },
        },
        theme.kind === 'perthnow' && {
            flex: '1 0 auto',
            overflow: 'hidden',
            height: 0,
            paddingTop: '56.25%',
            position: 'relative',

            [breakpoint('sm')]: {
                width: '20%',
            },
        },
    ],
)

export const StyledImage = styled('img')(
    {
        width: '100%',
        height: 'auto',
        display: 'block',
    },
    ({ theme }) => [
        theme.kind === 'thewest' && {
            filter: 'grayscale(100%)',
            width: '100%',
            height: 'auto',

            // On desktop make the image rounded for the west
            [breakpoint('sm')]: {
                position: 'absolute',
                transform: `translate(${calcRem(-desktopImageSize / 4)})`, // Offset so the image centres.
                width: 'auto',
                height: '100%',
            },
        },
        theme.kind === 'sevennews' && {
            position: 'absolute',
            transform: `translate(${calcRem(-111 / 4)})`, // Offset so the image centres.
            width: 'auto',
            height: '100%',
        },
        theme.kind === 'perthnow' && {
            position: 'absolute',
            top: 0,
            left: 0,
        },
    ],
)

export const StyledSocialLink = styled(WebLink)({
    display: 'block',
    color: colors.sevennews.blueRibbon,
    fontSize: calcRem(14),
    fontWeight: 400,
    textDecoration: 'none',

    '&:hover': {
        textDecoration: 'underline',
    },
    '&:focus': {
        outline: `1px solid`,
        textDecoration: 'none',
    },
})

const SocialIconStyles: CSSObject = {
    width: calcRem(30),
    height: calcRem(30),
    padding: calcRem(6),
    margin: calcRem(10, 0, 10, 0),
    verticalAlign: 'middle',
    [breakpoint('sm')]: {
        margin: calcRem(12, 4, 12, 0),
    },
}

export const StyledFacebookIcon = styled(IconFacebook)(SocialIconStyles, {
    fill: colors.social.facebook,
})
export const StyledTwitterIcon = styled(IconTwitter)(SocialIconStyles, {
    fill: colors.sevennewsSocial.twitter,
})
export const StyledInstagramIcon = styled(IconInstagram)(SocialIconStyles, {
    fill: colors.social.instagram,
})
